import {
  DateTimeFormatter,
  convertToMSPTimeZone,
  withErrorBoundary,
  getTranslatedText
} from '@nile/components';
import { AI_AGENTS_CONSTANT_FEATURE_ID_MAP } from '@nile/components/src/Components/AI/helpers/constants';
import { getTimeAgo } from '@nile/components/src/Components/Notifications/time';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Card,
  DisplayText,
  TextStyle
} from '@superops/ui-design-system';
import Tippy from '@tippyjs/react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AttributesHandler from './AttributeHandler';

const AI_AGENT_ICON_NAME_MAP = {
  [AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE]: {
    icon: 'TriageAgent',
    name: 'Triage agent',
    lokaliseKey: 'common.triage.agent'
  }
};

const AIAuditLog = ({ audit, entityAttributesMap }) => {
  const intl = useIntl();
  const actorId = audit?.actorId;
  const agentIcon = AI_AGENT_ICON_NAME_MAP?.[actorId]?.icon;
  const lokaliseKey = AI_AGENT_ICON_NAME_MAP?.[actorId]?.lokaliseKey;
  const name = AI_AGENT_ICON_NAME_MAP?.[actorId]?.name;
  const agentName = lokaliseKey ? getTranslatedText(intl, lokaliseKey) : name;
  const operation = audit?.operation;
  const time = audit?.time;
  const entity = audit?.entity;
  const user = audit?.user;
  const attributes = audit?.data?.[0]?.attributes;

  const getActionPrefixAndAction = () => {
    if (operation === 'AI_SUGGESTION') {
      if (entity === 'TicketConversation') {
        return { actionPrefix: 'mail suggestion', action: 'COMPOSED' };
      }

      if (entity === 'Ticket') {
        const isTechUpdate = attributes?.find(
          (el) => el?.name === 'technician'
        );
        if (isTechUpdate) {
          const isExit =
            isTechUpdate?.value?.old?.id ===
            AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE;
          if (isExit) {
            return { actionPrefix: null, action: 'EXITED' };
          }
          const isEntry =
            isTechUpdate?.value?.new?.id ===
            AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE;
          if (isEntry) {
            return { actionPrefix: null, action: 'ASSIGNED' };
          }
        }

        return { actionPrefix: 'field suggestions', action: 'APPROVED' };
      }
    }

    if (operation === 'UPDATE') {
      const isTechUpdate = attributes?.find((el) => el?.name === 'technician');
      if (isTechUpdate) {
        const isExit =
          isTechUpdate?.value?.old?.id ===
          AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE;
        if (isExit) {
          return { actionPrefix: null, action: 'EXITED' };
        }
        const isEntry =
          isTechUpdate?.value?.new?.id ===
          AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE;
        if (isEntry) {
          return { actionPrefix: null, action: 'ASSIGNED' };
        }
      }
    }

    return {
      actionPrefix: null,
      action: null
    };
  };

  const getUser = () => {
    if (operation === 'AI_SUGGESTION' && user?.name) {
      return user.name;
    }
    return null;
  };

  const { actionPrefix, action } = getActionPrefixAndAction();
  let userName;
  if (action !== 'ASSIGNED' && action !== 'EXITED') {
    userName = getUser() || 'System';
  }

  // ICON + Agent Name + actionPrefix (mail suggestion, field suggestions) + action (Assigned, Exited, Approved, Composed)+ user

  return (
    <Card noShadow padding="none">
      <div className="p10">
        <div className="fx actr g5 wrap">
          <div className="fx actr">
            <SVGIcon size="sm" name={agentIcon} />
          </div>
          <div>{agentName}</div>
          {actionPrefix && (
            <div className="lowercase">
              <FormattedMessage id={actionPrefix} />
            </div>
          )}
          {action && (
            <DisplayText element="p" size="medium" weight="SemiBold">
              <FormattedMessage id={action} />
            </DisplayText>
          )}

          {userName && (
            <>
              <div className="lowercase">
                <FormattedMessage id="common.keyword.by" />
              </div>

              <div className="fx actr g5">
                <Avatar
                  name={userName}
                  initials={userName[0]}
                  size="extraSmall"
                />
                <TextStyle variation="strong" weight="Regular">
                  {userName}
                </TextStyle>
              </div>
            </>
          )}
        </div>
        <span className="ml-26 d-in">
          <TextStyle variation="subdued">
            <Tippy
              content={
                <div className="p0510">
                  <DateTimeFormatter type="dateTime" date={time} />
                </div>
              }
              duration={250}
              delay={250}
              interactive
              placement="bottom-start"
              arrow={false}
              allowHTML
              theme="dark"
              appendTo={document.body}
            >
              <div>
                {getTimeAgo(time.toString(), convertToMSPTimeZone(new Date()))}
              </div>
            </Tippy>
          </TextStyle>
        </span>

        {attributes && (
          <Card noShadow padding="none" variant="secondaryCard">
            <div className="px-25 pt10 pb10 mt5">
              <AttributesHandler
                skipName={false}
                showInitial={false}
                attributes={attributes}
                entityAttributesMap={entityAttributesMap || {}}
                entityName={entity}
              />
            </div>
          </Card>
        )}
      </div>
    </Card>
  );
};

export default withErrorBoundary(AIAuditLog);
